import { useCallback, useEffect, useState } from 'react';
import { NameHelper } from '../../../shared/utils/name-helper';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import LogoGestore from '../../../assets/gestore.svg';
import './style.css';
import { fetchUserByIdService, getMyRoutesAccessService } from '../../../features/user/service/user-service';
import { User } from '../../../shared/interfaces/user';
import AddCardIcon from '@mui/icons-material/AddCard';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNotificationContext } from '../../../shared/context/notification-context';

interface RoutesAccess {
    allRoutes: string[];
    accessLevel: string;
}

const MenuTop = () => {
    const location = useLocation();
    const { pathname } = location;
    const [userInitials, setUserInitials] = useState<string>('');
    const [routesAccess, setRoutesAccess] = useState<RoutesAccess | null>(null);
    const navigate = useNavigate();
    const [storedUser, setStoredUser] = useState<User | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    // Usando o contexto de notificações
    const { unreadCount, isLoadingCount, updateUnreadCount } = useNotificationContext();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = (url: string) => {
        setAnchorEl(null);
        navigate(url);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/');
    };

    const fetchRoutesAccess = useCallback(async (userData: User) => {
        try {
            const response = await getMyRoutesAccessService(userData.id);
            setRoutesAccess(response);
        } catch (error) {
            console.error('Erro ao buscar rotas de acesso:', error);
        }
    }, []);

    const fetchCompleteUser = useCallback(async (userData: User) => {
        try {
            const response = await fetchUserByIdService(userData.id);
            setStoredUser(response);
        } catch (error) {
            console.error('Erro ao buscar dados completos do usuário:', error);
            setStoredUser(userData);
        }
    }, []);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}') as User;
    
        if (userData?.name) { 
            setUserInitials(NameHelper(userData.name));
        }

        if (userData?.id) {
            fetchRoutesAccess(userData);
            fetchCompleteUser(userData);
        } else {
            setStoredUser(userData);
        }
    }, [pathname, fetchCompleteUser, fetchRoutesAccess]);

    // Atualizar contador quando sair da tela de notificações
    useEffect(() => {
        if (pathname !== '/notificacao') {
            updateUnreadCount();
        }
    }, [pathname, updateUnreadCount]);

    const hasRoute = (routeName: string): boolean => {
        return routesAccess?.allRoutes?.includes(routeName.toLowerCase()) || false;
    };

    const formatBadgeCount = (count: number): string => {
        if (count === 0) return '';
        if (count > 99) return '99+';
        return count.toString();
    };

    const roleDescription = String(storedUser?.role?.description || storedUser?.role?.name || '').toLowerCase();
    const canOpenNotificationSettings = roleDescription.includes('super-admin') || roleDescription.includes('financeiro');

    return (
        <div className="menu-top_container">
            <img src={LogoGestore} alt="Logo causale gestore" className="menu-top_container__company-logo" />

            <div className="menu-top_container__menus">
                <Link to="/dashboard">
                    <DashboardOutlinedIcon 
                        className='icons'
                        sx={{ fontSize: 40, color: 'primary' }}
                        data-tooltip-id="dashboard-tooltip"
                        data-tooltip-content="Listar Projetos"
                    />
                </Link>
                <Tooltip id="dashboard-tooltip" />

                <Link to="/notificacao" className="notification-link">
                    <div className="notification-icon-container">
                        <NotificationsIcon 
                            className="icons" 
                            sx={{ fontSize: 40, color: 'primary' }} 
                            data-tooltip-id="notification-tooltip" 
                            data-tooltip-content={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
                        />
                        {unreadCount > 0 && !isLoadingCount && (
                            <span className="notification-badge">
                                {formatBadgeCount(unreadCount)}
                            </span>
                        )}
                    </div>
                </Link>
                <Tooltip id="notification-tooltip" />

                {hasRoute('projeto') && (
                    <>
                        <Link to="/projeto">
                            <CreateNewFolderIcon 
                                className="icons" 
                                sx={{ fontSize: 40, color: 'primary' }} 
                                data-tooltip-id="folder-tooltip" 
                                data-tooltip-content="Adicionar Projeto"
                            /> 
                        </Link>
                        <Tooltip id="folder-tooltip" />
                    </>
                )}

                {hasRoute('financeiro') && (
                    <>
                        <Link to="/financeiro">
                            <AddCardIcon 
                                className="icons" 
                                sx={{ fontSize: 40, color: 'primary' }} 
                                data-tooltip-id="billing-tooltip" 
                                data-tooltip-content="Adicionar Movimentação"
                            /> 
                        </Link>
                        <Tooltip id="billing-tooltip" />
                    </>
                )}

                <Button 
                    className="menu-top_container__user-initials"
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                > 
                    {userInitials} 
                </Button>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    <MenuItem onClick={() => handleClose('/meus-dados')}>Meus dados</MenuItem>
                    {hasRoute('configuracoes') && (
                        <MenuItem onClick={() => handleClose('/planos')}>Planos</MenuItem>
                    )}
                    {hasRoute('configuracoes') && (
                        <MenuItem onClick={() => handleClose('/clientes')}>Clientes</MenuItem>
                    )}
                    {hasRoute('usuarios') && (
                        <MenuItem onClick={() => handleClose('/usuarios')}>Usuários</MenuItem>
                    )}
                    {hasRoute('beneficiarios') && (
                        <MenuItem onClick={() => handleClose('/beneficiarios')}>Beneficiários</MenuItem>
                    )}
                    {canOpenNotificationSettings && (
                        <MenuItem onClick={() => handleClose('/configuracao-notificacoes')}>Envio de notificações</MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
            </div>
        </div>
    );
};

export default MenuTop;
